// @flow

import { createSelector } from 'reselect';
import { superpathjoin } from 'superpathjoin';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import { selectFromConfig } from '@gandi/redux-config';
import { selectCurrentId } from '@gandi/redux-sharing';
import { localeSelector as selectLocale } from '@gandi/translate';

import type { FullState } from '../../types';

export const selectShopURL: (state: FullState) => string =
  selectFromConfig<string>('app.layout.urls.shop', 'https://shop.gandi.net');

export const selectAdminURL: (state: FullState) => string =
  selectFromConfig<string>('app.layout.urls.admin', 'https://admin.gandi.net');

const selectBaseWebURL: (state: FullState) => string = selectFromConfig<string>(
  'app.layout.urls.public_site',
  'https://www.gandi.net',
);
export const selectWebURL: (state: FullState) => string = createSelector(
  [selectBaseWebURL, selectLocale],
  (wwwUrl, locale) => `${wwwUrl}/${locale}`,
);

export const selectLoginEndpoint: (state: FullState) => string =
  selectFromConfig<string>('app.layout.urls.id', 'https://id.gandi.net');

const selectBaseWhoisUrl = selectFromConfig<string>(
  'app.layout.urls.whois',
  'https://whois.gandi.net',
);
export const selectWhoisUrl: (state: FullState) => string = createSelector(
  [selectBaseWhoisUrl, selectLocale],
  (whois, locale) => `${whois}/${locale}/results`,
);

/**
 * Compute and return a fully qualified URL, including the current app context.
 * Example call: `selectShopContextURL(state, 'payment-return')`
 * Example output: `https://shop.gandi.net/fr/[{sharingId}]/payment-return`
 */
export const selectShopContextURL: (state: FullState, to?: string) => string =
  createSelector(
    [selectShopURL, selectLocale, selectCurrentId, (_, to) => to || ''],
    (shopUrl, ...paths) => superpathjoin(shopUrl, ...paths),
  );

export const selectAdminBillingUrl = (state: FullState): string => {
  const adminUrl = selectAdminURL(state);
  return superpathjoin(adminUrl, 'billing', selectCurrentId(state));
};

export const selectOrderUrl = (
  state: FullState,
  { orderId }: { orderId: string, ... },
): string => {
  const baseUrl = selectAdminBillingUrl(state);
  return superpathjoin(baseUrl, 'orders', orderId);
};

/** Return the URL of the "GandiCloud VPS" admin. */
const selectAdminGostackURL: (state: FullState) => string = createSelector(
  [selectAdminURL, selectCurrentId],
  (adminUrl, currentId) =>
    currentId ? `${adminUrl}/gostack/${currentId}` : `${adminUrl}/gostack`,
);

/** Return either the URL of the "GandiCloud VPS" admin or public page. */
export const selectGostackURL: (state: FullState) => string = createSelector(
  [selectAdminGostackURL, selectWebURL, selectIsAuthenticatedUser],
  (adminGostackUrl, wwwUrl, isAuthenticated) =>
    isAuthenticated ? adminGostackUrl : `${wwwUrl}/cloud/vps`,
);
